import SiteLogo from '@/components/Logo'
import SitePhoto from '@/components/Photo'
import type { Section } from '@/content/sections'
import { TestimonialCard } from '@/pages/Testimonials'
import { localizeHref } from '@/i18n/href'
import { useI18n } from '@/i18n/I18nProvider'
import { useState } from 'react'
import { Ctas, type SectionOf } from './shared'

/**
 * Customer quotes in the **Hall of Fame card**, one at a time, behind a tab per
 * company.
 *
 * Asked for on 2026-08-28 for the partner page, with a screenshot of
 * digitopia.co's own partner testimonials — three company names in a row, the
 * active one underlined, one quote beneath them — and the instruction that ours
 * should be *"hall of fame tasarımının aynısı"*. So the card is not rebuilt
 * here: `pages/Testimonials.tsx`'s `TestimonialCard` is imported and drawn as
 * it stands, mark, portrait, arc and all. Two copies of that template would
 * drift the first time either page is touched, and the whole point of the Hall
 * of Fame being a template was that a card is data.
 *
 * **Tabs and not the arrows `FeatureCarousel` uses**, which is the one place
 * this departs from the carousel already on the site. There the panes are three
 * arguments with nothing to name them, so a numbered position is all a control
 * could say; here every pane *is* a company, the reference labels them, and a
 * reader picking the partner they recognise beats stepping through three.
 *
 * **The tabs live inside the card, in the eyebrow's slot** (2026-08-28 —
 * *"seçenekler gri kutucuğun içinden olsun"*). They shipped centred above the
 * grey card the way the reference sets them, which meant the active company was
 * written twice in the same uppercase blue three lines apart: once as the live
 * tab and once as the card's own eyebrow. Now the active tab is that eyebrow.
 * The card takes them through its `header` prop, so the Hall of Fame page and
 * the story page — which pass nothing — are unchanged.
 *
 * `storyLink={false}` because a partner is not a customer story: the pill on the
 * Hall of Fame card opens `/en/customers/<slug>` and none of these three has
 * one. The card already handles that being off — the story page reuses it the
 * same way.
 *
 * Mechanism copied from `FeatureCarousel` deliberately: **every pane rendered,
 * stacked in one grid cell**, with only opacity changing. The 1rem lift that
 * carousel uses went when the tabs moved inside the card: the strip is drawn in
 * every pane, so a lift would slide the control the reader just clicked out
 * from under their cursor. A crossfade leaves the tabs where they are and moves
 * only the colour. It costs
 * JavaScript, which written pages can afford (`prerender.tsx` sets
 * `hydrate: true` for them) and the 117 imported documents cannot — do not
 * carry this onto one of those. It also means all three quotes are in the
 * prerendered HTML rather than only the first, so a crawler and a reader with no
 * JavaScript get the words; and because grid children stretch to the row, the
 * three cards are one height and the portraits all stand on the same edge.
 */
/** A quote is one string or several; every renderer wants the paragraphs. */
function quoteParagraphs(quote: string | string[]): string[] {
  return Array.isArray(quote) ? quote : [quote]
}

/**
 * The Hall of Fame card, pared back and two across — `variant: 'minimal'`.
 *
 * Everything that makes the full card a full-width object is gone: the arc, the
 * standing cut-out figure and the `sm:py-14` padding. What is left is the part
 * that identifies the speaker — the blue eyebrow with its short rule, the mark,
 * the words, and the face beside the name. That avatar is why this works in half
 * a column: it is the full card's `PortraitAvatar` circle, drawn 80px here
 * against its 64 (2026-08-29, *"görseller de 0.2x büyüyebilir"*), so a reader
 * who meets both treatments sees one design at two scales rather than two
 * designs.
 *
 * The pill is per item rather than per section, because each card points at its
 * own customer's story. It is the card's own `btn-primary`, one size down, as on
 * the Hall of Fame.
 */
function MinimalQuoteCard({
  q,
  locale,
  header,
  tabbable = true,
  sideBySide = false,
  secondaryCta,
}: {
  q: Extract<Section, { kind: 'quotes' }>['items'][number]
  locale: string
  /** Replaces the eyebrow and its rule — the tab strip, on the carousel. */
  header?: React.ReactNode
  /** False for a hidden carousel pane, so its link is off the tab order. */
  tabbable?: boolean
  /** True in the two-across grid, where the marks are compared with each other. */
  sideBySide?: boolean
  /**
   * The section's own link, drawn beside the card's pill as the quieter of the
   * two. Asked for on 2026-08-30 (*"bütün success storilere götüren buton
   * kutucuk içinde ikincil CTA olsun"*): it used to sit under the card as a
   * lone blue pill, which read as the section's main action while the actual
   * one — this customer's story — was the smaller button above it. Inside the
   * card the pair is read together and the ranking is the styling, not the
   * position.
   */
  secondaryCta?: { label: string; href: string }
}) {
  return (
    <article className="flex h-full flex-col rounded-3xl bg-band px-7 py-9 sm:px-10 sm:py-11">
      {header ?? (
        <>
          {/* The company name is `Testimonials.tsx`'s `CompanyEyebrow`, class
              for class: body colour at rest, brand blue under the pointer, and
              no underline in either state (2026-09-02 — *"idle hali siyah,
              üzerine gelince smooth şekilde maviye dönsün, altında çizgi
              çıkmasın"*, pointing at the service headings on the home page as
              the model). It used to be blue at rest with `hover:underline`,
              which is the Hall of Fame's *hover* colour showing before anyone
              has hovered, so nothing distinguished a card you can open from one
              you cannot — the same reasoning that settled the treatment on that
              card the day before. Colour is the only thing that moves, so it
              works on the documents that never hydrate. */}
          {q.cta ? (
            <a
              href={localizeHref(q.cta.href, locale)}
              tabIndex={tabbable ? undefined : -1}
              className="group inline-block no-underline"
            >
              {/* `lang="en"` because `uppercase` follows the document's
                  language, and on a Turkish page that maps `i` to `İ` — "LC
                  Waikiki" came out "LC WAİKİKİ". */}
              <span
                lang="en"
                className="block text-sm font-bold uppercase tracking-[0.14em] text-foreground transition-colors group-hover:text-primary group-focus-visible:text-primary"
              >
                {q.company ?? q.source}
              </span>
            </a>
          ) : (
            <p lang="en" className="text-sm font-bold uppercase tracking-[0.14em] text-foreground">
              {q.company ?? q.source}
            </p>
          )}
          {/* No short blue rule under the company name: asked out on 2026-09-01,
              *"bu küçük mavi çizginin çıkmasına gerek yok, sadece mavi olmak ve
              ufak çaplı değişimler yeterli"*. The name is blue, and that is the
              whole treatment. Same change in `Testimonials.tsx`. */}
        </>
      )}
      {q.logo && (
        // `fit="card"` in the two-across grid and `"quote"` everywhere else.
        // Side by side, the two marks are compared with each other rather than
        // with a card a screen further down, so the slot's height is what they
        // share — one width there draws a padded square badge overflowing the
        // slot at about half the wordmark's ink beside it. A carousel shows one
        // card in the full measure and keeps the width rule it shipped with.
        // The two-across slot is `h-28`, and it can be that short because the
        // marks in it are now cropped to their ink rather than drawn with their
        // canvas (see `logo.ink` in `content/sections.ts`). `h-48` came from
        // 2026-08-29's *"logolar daha büyük olmalı 2x daha büyük"* and did make
        // the ink bigger, but it made the transparent canvas bigger with it, so
        // the card read as a mark stranded in a hole. Cropping keeps the ink at
        // roughly the size that asked for while the hole goes away; the slot
        // only has to be as tall as the taller of the two cropped marks so both
        // cards' quotes still start on the same line. The margins are back to
        // real spacing now that the file is not supplying its own.
        //
        // `items-end` in that shared slot, not `items-center`: LC Waikiki's
        // wordmark crops to about half Litehouse's badge height, and centring
        // both in one box splits the difference into equal dead air above and
        // below the shorter mark — the "boşluk" a screenshot pointed at on
        // 2026-09-01. Bottom-aligning collapses that into one gap, above the
        // mark and below the eyebrow rule, which reads as the section's own
        // spacing rather than a hole inside the card.
        //
        // 72px, down from `h-28`'s 112: the slot only has to be as tall as the
        // taller of the two cropped marks, and once both declare their ink
        // width as well as their ink height (see `logo.inkWidth` in
        // `content/sections.ts`) the taller of them is 71px rather than the
        // 105px of mostly-empty canvas the old fraction claimed. The extra
        // 40px was the band of nothing between the eyebrow rule and the marks
        // in that screenshot; it was slot, not file.
        // 90px, up from 72: the slot is exactly as tall as the taller cropped
        // mark and no taller, so any spare height would print as the band of
        // nothing this section has twice been asked to remove. Litehouse's
        // badge carries `inkScale: 1.3` now (2026-09-01 — *"Litehouse un
        // logosu daha büyük olmalı"*), which takes its clip box from 71px to
        // 89, so the slot follows it. `items-end` is what keeps the request's
        // other half: the marks grow upward off a shared bottom edge, so the
        // pair stays on one plane and both quotes still start on the same line.
        <p className={`flex ${sideBySide ? 'items-end mt-7 h-[90px]' : 'items-center mt-8 h-16'}`}>
          {q.cta ? (
            <a href={localizeHref(q.cta.href, locale)} tabIndex={tabbable ? undefined : -1}>
              <SiteLogo logo={q.logo} fit={sideBySide ? 'card' : 'quote'} />
            </a>
          ) : (
            <SiteLogo logo={q.logo} fit={sideBySide ? 'card' : 'quote'} />
          )}
        </p>
      )}
      <blockquote className={q.logo ? (sideBySide ? 'mt-7' : 'mt-6') : 'mt-8'}>
        {quoteParagraphs(q.quote).map((paragraph, i) => (
          <p key={i} className={`leading-relaxed${i ? ' mt-4' : ''}`}>
            {paragraph}
          </p>
        ))}
      </blockquote>
      {/*
        The face, the name and the pill are ONE bottom-anchored block, and the
        row that holds the face has a fixed height. `mt-auto` on the attribution
        alone was not enough: it pinned the pill, but the attribution row is as
        tall as whatever is in it, so a two-line name — "Şerafettin Özer, Chief
        Digital and Information Officer" wraps in half a column where "Ben Bryan,
        Manager, Applications" does not — lifted one card's portrait a line above
        its neighbour's while the button stayed put. `h-24` is the avatar's own
        height plus room for the second line, so every element below the quote
        sits on the same line on both cards whatever the quote and the title do.
      */}
      <div className="mt-auto pt-8">
        {/* The face and the name open the same story the card's pill does, when
            there is one (2026-09-02 — *"sadece tıklanabilir ve kendi sayfalarına
            yönlendirilebilir olsunlar ancak bu hiçbir mouse over eklentisi vs
            anlamına gelmesin"*). So this is an anchor and nothing else: no
            hover colour, no underline, no cursor treatment beyond the browser's
            own, and the class list on the row is the one it had. Tailwind's
            preflight already makes an `a` inherit colour and decoration, and
            `no-underline text-inherit` say so out loud so a later base style
            cannot quietly give this one a link's appearance. */}
        {q.cta ? (
          <a
            href={localizeHref(q.cta.href, locale)}
            tabIndex={tabbable ? undefined : -1}
            className="flex h-24 items-center gap-4 no-underline text-inherit"
          >
            {q.portrait && (
              <span className="block size-20 shrink-0 overflow-hidden rounded-full">
                <SitePhoto photo={q.portrait} sizes="80px" className="size-full object-cover" />
              </span>
            )}
            <p className="font-bold">{q.source}</p>
          </a>
        ) : (
          <div className="flex h-24 items-center gap-4">
            {q.portrait && (
              <span className="block size-20 shrink-0 overflow-hidden rounded-full">
                <SitePhoto photo={q.portrait} sizes="80px" className="size-full object-cover" />
              </span>
            )}
            <p className="font-bold">{q.source}</p>
          </div>
        )}
        {(q.cta || secondaryCta) && (
          <div className="flex flex-wrap items-center gap-4 pt-6">
            {q.cta && (
              <a
                href={localizeHref(q.cta.href, locale)}
                tabIndex={tabbable ? undefined : -1}
                className="btn-primary px-8 py-3 text-base"
              >
                {q.cta.label}
              </a>
            )}
            {secondaryCta && (
              <a
                href={localizeHref(secondaryCta.href, locale)}
                tabIndex={tabbable ? undefined : -1}
                className="btn-secondary px-8 py-3 text-base"
              >
                {secondaryCta.label}
              </a>
            )}
          </div>
        )}
      </div>
    </article>
  )
}

function MinimalQuoteCards({
  heading,
  items,
}: {
  heading?: string
  items: Extract<Section, { kind: 'quotes' }>['items']
}) {
  const { locale } = useI18n()
  return (
    <>
      {heading && (
        <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">{heading}</h2>
      )}
      <div className="mt-12 grid items-stretch gap-8 lg:grid-cols-2">
        {items.map((q) => (
          <MinimalQuoteCard
            key={q.source + quoteParagraphs(q.quote)[0].slice(0, 30)}
            q={q}
            locale={locale}
            sideBySide
          />
        ))}
      </div>
    </>
  )
}

/**
 * The minimal card, one company at a time — `variant: 'minimal-carousel'`.
 *
 * `QuoteCarousel`'s mechanism exactly (every pane prerendered and stacked in one
 * grid cell, only opacity changing, the tab strip drawn inside each pane in the
 * eyebrow's slot so nothing moves under the cursor), with `MinimalQuoteCard`
 * where the full Hall of Fame card goes. That combination is what Strategy
 * Consulting's Proof section asked for: the Hall of Fame's design, pared back,
 * switchable, and with each card's own "Read more" going to that customer's
 * story rather than one button under the pair.
 *
 * `max-w-3xl` because a single card across the full measure sets a short quote
 * as three wide lines; two across never had that problem.
 */
function MinimalQuoteCarousel({
  heading,
  items,
  cta,
}: {
  heading?: string
  items: Extract<Section, { kind: 'quotes' }>['items']
  /** The section link, drawn inside the card as its secondary button. */
  cta?: { label: string; href: string }
}) {
  const { locale } = useI18n()
  const [active, setActive] = useState(0)
  const count = items.length
  if (!count) return null
  const current = Math.min(active, count - 1)

  return (
    <>
      {heading && (
        <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">{heading}</h2>
      )}
      <div className="mt-10 grid max-w-3xl items-stretch" aria-live="polite">
        {/* The grey box itself, drawn once and never animated — see the same
            layer in `QuoteCarousel` below for why it is here. */}
        <div
          aria-hidden
          className="pointer-events-none col-start-1 row-start-1 h-full rounded-3xl bg-band"
        />
        {items.map((q, i) => (
          <div
            key={q.source + quoteParagraphs(q.quote)[0].slice(0, 30)}
            // Same pinning and the same staggered crossfade as `QuoteCarousel`
            // above, and for the same two reasons: the pane is pinned to the row
            // and the card to the pane, so the grey box is one size whichever
            // company is showing. Same overlapping handover as `QuoteCarousel`
            // above, and for the same reason: with a gap between the panes the
            // card stood empty for a moment.
            className={`col-start-1 row-start-1 h-full transition-[opacity,transform] motion-reduce:transition-none [&>article]:h-full ${
              i === current
                ? 'opacity-100 translate-y-0 ease-out [transition-duration:450ms]'
                : 'pointer-events-none opacity-0 translate-y-1 ease-in [transition-duration:200ms]'
            }`}
            aria-hidden={i !== current}
          >
            <MinimalQuoteCard
              q={q}
              locale={locale}
              tabbable={i === current}
              secondaryCta={cta}
              header={
                <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
                  {items.map((t, j) => (
                    <button
                      key={t.source + quoteParagraphs(t.quote)[0].slice(0, 30)}
                      type="button"
                      onClick={() => setActive(j)}
                      tabIndex={i === current ? 0 : -1}
                      aria-current={j === current}
                      lang="en"
                      // Same as the Hall of Fame strip: blue marks the live tab,
                      // no rule under it — and `outline-none` in every state,
                      // so the browser's own focus outline is not drawn under
                      // the tab the reader just clicked. The keyboard ring
                      // stays.
                      className={`text-sm font-bold uppercase tracking-[0.14em] outline-none transition duration-200 focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4 ${
                        j === current ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {/* `lang="en"` so CSS `uppercase` on a Turkish page
                          spells a company name with a plain "I", not the
                          Turkish-locale "İ" — see the same fix in
                          `sections/tool.tsx`. */}
                      <span lang="en">{t.company ?? t.source}</span>
                    </button>
                  ))}
                </div>
              }
            />
          </div>
        ))}
      </div>
    </>
  )
}

function QuoteCarousel({
  heading,
  items,
}: {
  heading?: string
  items: Extract<Section, { kind: 'quotes' }>['items']
}) {
  const { locale } = useI18n()
  const [active, setActive] = useState(0)
  const count = items.length
  if (!count) return null
  const current = Math.min(active, count - 1)

  return (
    <>
      {heading && (
        <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">{heading}</h2>
      )}

      {/* The tab row, drawn in the card's eyebrow slot — see `header` on
          `TestimonialCard`. An uppercase tracked label is the eyebrow this site
          already uses for a company name, so the active tab *is* the eyebrow:
          accent colour and nothing else. It sits where the name sat — left, on the card's own padding
          — rather than centred above the card, because the name was written
          twice when it was in both places. */}
      <div className="mt-10 grid items-stretch" aria-live="polite">
        {/*
          The grey box, drawn ONCE, under the panes, and never animated
          (2026-09-07 — *"kutucuk yeni bir seçeneğe tıkladığımızda kaybolup geri
          geliyor … kutucuk kaybolup geri gelmesin"*). The staggered crossfade
          fades a whole pane out and the next one in, and the `bg-band` card is
          part of the pane — so for the length of the handover there was no card
          on screen at all and the section collapsed to the page background
          before rebuilding itself. This layer is the same `rounded-3xl bg-band`
          at the same grid cell, so the box is continuously painted and only its
          contents cross over it: the switch reads as the words changing inside a
          box that never moves. It is empty, so it adds nothing to the row's
          height, and it needs no JavaScript — the imported documents that never
          hydrate would show it as the card they already show.
        */}
        <div
          aria-hidden
          className="pointer-events-none col-start-1 row-start-1 h-full rounded-3xl bg-band"
        />
        {items.map((q, i) => (
          <div
            key={quoteParagraphs(q.quote)[0].slice(0, 30)}
            // `[&>article]:h-full` is what keeps the grey card one size across
            // the tabs (2026-09-04 — *"kutucuk sabit olmalı, geçişler arasında
            // büyüklüğü değişmemeli"*). The panes are stacked in one grid cell
            // and the cell is as tall as the longest quote, but the pane only
            // stretched itself: the card inside kept its own natural height, so
            // switching from a four-line quote to a three-line one shrank the
            // card — and with it the arc and the standing figure, which are
            // sized off the card. `MinimalQuoteCard` already carried `h-full`
            // for this reason; the Hall of Fame card is shared with pages that
            // draw it in a normal block flow, where a percentage height
            // resolves to auto, so the class belongs here rather than on it.
            // **The panes overlap; there is no gap between them** (2026-09-07 —
            // *"yine de bir şeylerin olmadığı bir kutu görüyoruz … o şekilde bir
            // geçiş olmasın"*). A staggered fade — outgoing, then a pause, then
            // incoming — is what put an empty card on screen: for the length of
            // the pause the box was painted with nothing in it, which is what
            // the persistent grey layer made visible rather than fixed. So the
            // incoming pane now starts immediately and the outgoing one leaves
            // quickly under it: 200ms out `ease-in` against 450ms in `ease-out`,
            // so the old quote is at a tenth of its opacity by the time the new
            // one is legible and the two are never readable through each other
            // — the flicker the stagger was introduced to avoid. The incoming
            // pane also rises 4px into place, so the change reads as movement
            // rather than as a light coming back on, and the tab strip inside
            // the pane moves too little to shift under the cursor that just
            // clicked it. `h-full` on the pane is the belt to
            // `[&>article]:h-full`'s braces — the card is sized off the pane, so
            // the pane is pinned to the row before the card is pinned to the
            // pane, and neither can fall back to its own content height.
            className={`col-start-1 row-start-1 h-full transition-[opacity,transform] motion-reduce:transition-none [&>article]:h-full ${
              i === current
                ? 'opacity-100 translate-y-0 ease-out [transition-duration:450ms]'
                : 'pointer-events-none opacity-0 translate-y-1 ease-in [transition-duration:200ms]'
            }`}
            // A hidden pane is still in the layout, and now it holds a copy of
            // the tab strip: every pane carries the whole control, and the one
            // the reader can see is the one that is live. `pointer-events-none`
            // takes the mouse and `tabIndex={-1}` the keyboard, so a hidden
            // pane's buttons cannot be reached — `aria-hidden` alone is not
            // enough once there is something focusable inside.
            aria-hidden={i !== current}
          >
            <TestimonialCard
              locale={locale}
              storyLink={false}
              // Each pane's own pill, when the item carries one: the company in
              // that tab has a success story and the reader is looking straight
              // at it (2026-09-01, *"her şirketin kendi succes story sayfasına
              // yönlendiren bir buton olsun"*). `storyLink` stays off because
              // these quotes are page copy, not Hall of Fame entries, so there
              // is no catalogue slug to resolve — the href is in the item. A
              // section without item ctas (the partner page, the e-books) draws
              // no pill, exactly as before.
              cta={q.cta}
              tabbable={i === current}
              header={
                // Held to the same measure as the quote below it
                // (`max-w-[34rem]` on the card's text column), so the strip
                // stays on the left, under the same left edge as the mark and
                // the words, and never reaches the figure however many
                // partners the section carries (2026-08-30, *"nexgen
                // insanların kafasına çok yakın duruyor"*). At three partners
                // that measure used to wrap after the first name — one tab
                // alone on its own line above the other two — which reads
                // worse than the crowding it was fixed to avoid. `text-xs`
                // and a tighter `tracking`/`gap-x` (2026-09-01) buy back
                // enough width for all three to sit on one row within the
                // same max-width, so the boundary that protects the portrait
                // does not move.
                //
                // That was still not enough: a screenshot on 2026-09-02 showed
                // the same wrap, Fisher alone above the other two, so the type
                // goes down one more step (11px, `tracking-[0.05em]`,
                // `gap-x-3`) and the measure up to 36rem. 36rem is still short
                // of where the standing figure begins — `Portrait` is
                // `right-12` and `size-[28rem]`, so its left edge is ~608px
                // from the card's content edge against this strip's 576 — and
                // the wrap is kept rather than swapped for `flex-nowrap`, so a
                // fourth partner drops to a second line instead of walking
                // into the portrait. `whitespace-nowrap` only stops a single
                // company name from breaking mid-name.
                <div className="flex max-w-[36rem] flex-wrap items-center gap-x-3 gap-y-2">
                  {items.map((t, j) => (
                    <button
                      key={quoteParagraphs(t.quote)[0].slice(0, 30)}
                      type="button"
                      onClick={() => setActive(j)}
                      tabIndex={i === current ? 0 : -1}
                      aria-current={j === current}
                      lang="en"
                      // No rule under the live tab: the blue says it on its own
                      // (2026-09-04 — *"seçili olan seçeneğe gelince mavilik
                      // yeter, altındaki çizgiye gerek yok"*), the same call
                      // that took the short blue rule off the company eyebrow.
                      // Nothing shifts when the reader switches, because colour
                      // was always the only thing that moved — the inactive
                      // tabs carried a transparent rule of the same weight.
                      //
                      // **`outline-none` unconditionally, and that is the line
                      // that was still being drawn under the chosen company.**
                      // The markup has carried no rule of its own since that
                      // change, but a clicked `button` keeps DOM focus, and the
                      // outline suppressed here was suppressed only in the
                      // `:focus-visible` state — so on the browsers that treat a
                      // mouse click on a button as focus-visible (Safari and
                      // WebKit-based mobile among them), the UA's own accent
                      // outline was painted around the tab the reader had just
                      // pressed and read as a blue underline beneath it. The
                      // keyboard ring below is unchanged, so tabbing through the
                      // strip still shows where focus is.
                      className={`whitespace-nowrap text-[11px] font-bold uppercase tracking-[0.05em] outline-none transition duration-200 focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4 ${
                        j === current ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {/* `lang="en"` so CSS `uppercase` on a Turkish page
                          spells a company name with a plain "I", not the
                          Turkish-locale "İ" — see the same fix in
                          `sections/tool.tsx`. */}
                      <span lang="en">{t.company ?? t.source}</span>
                    </button>
                  ))}
                </div>
              }
              testimonial={{
                company: q.company ?? q.source,
                logo: q.logo,
                portrait: q.portrait,
                quote: quoteParagraphs(q.quote),
                attribution: q.source,
              }}
            />
          </div>
        ))}
      </div>
    </>
  )
}

export default function QuotesSection({ section }: { section: SectionOf<'quotes'> }) {
  if (section.variant === 'fame') {
    return (
      <>
        <QuoteCarousel heading={section.heading} items={section.items} />
        {section.cta && <Ctas items={[section.cta]} />}
      </>
    )
  }
  if (section.variant === 'minimal-carousel') {
    // The section's `cta` goes inside the card beside each customer's "Read
    // more" rather than under it, so it is not repeated below as well.
    return <MinimalQuoteCarousel heading={section.heading} items={section.items} cta={section.cta} />
  }
  if (section.variant === 'minimal') {
    return (
      <>
        <MinimalQuoteCards heading={section.heading} items={section.items} />
        {section.cta && <Ctas items={[section.cta]} />}
      </>
    )
  }
  return (
    <>
      {section.heading && (
        <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">{section.heading}</h2>
      )}
      <div className="mt-12 grid gap-10 lg:grid-cols-2">
        {section.items.map((q) => (
          <figure key={quoteParagraphs(q.quote)[0].slice(0, 30)}>
            <blockquote className="text-xl leading-relaxed">
              “{quoteParagraphs(q.quote).join(' ')}”
            </blockquote>
            <figcaption className="mt-4 text-sm font-semibold uppercase tracking-[0.12em] text-muted-foreground">
              {q.source}
            </figcaption>
          </figure>
        ))}
      </div>
      {section.cta && <Ctas items={[section.cta]} />}
    </>
  )
}
